// PlayerData API Service - PHP Backend Integration
// Calls PHP backend to avoid CORS issues

// Dynamically determine API URL based on environment
const getApiBaseUrl = () => {
  // Check if running in production (domain-based detection)
  const isProduction =
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1" &&
    !window.location.hostname.includes("local");

  if (isProduction) {
    // Production: use current domain's backend path (in /personal-training-BE/ folder)
    return `${window.location.origin}/personal-training-BE/back-end-endpoint-playerdata.php`;
  } else {
    // Development: use localhost with MAMP port (at root level)
    return "http://localhost:8888/back-end-endpoint-playerdata.php";
  }
};

const API_BASE_URL = getApiBaseUrl();

export class PlayerDataApiService {
  /**
   * Make a request to the PHP backend
   */
  async makeRequest(action, params = {}) {
    try {
      const url = new URL(API_BASE_URL, window.location.origin);

      // Add action parameter
      url.searchParams.append("action", action);

      // Add other parameters for GET requests
      if (Object.keys(params).length > 0) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            url.searchParams.append(key, value);
          }
        });
      }

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Unknown error occurred");
      }

      return result.data;
    } catch (error) {
      console.error("PlayerData API error:", error);
      throw error;
    }
  }

  /**
   * Test the API connection
   */
  async testConnection() {
    return await this.makeRequest("test");
  }

  /**
   * Get all teams for the organization
   */
  async getTeams() {
    return await this.makeRequest("getTeams");
  }

  /**
   * Get sessions for a specific team
   */
  async getTeamSessions(teamId) {
    return await this.makeRequest("getTeamSessions", { teamId });
  }

  /**
   * Get GPS data for a session
   */
  async getSessionGpsData(sessionId) {
    return await this.makeRequest("getSessionGpsData", { sessionId });
  }

  /**
   * Get athlete performance metrics
   */
  async getAthleteMetrics(sessionId) {
    return await this.makeRequest("getAthleteMetrics", { sessionId });
  }

  /**
   * Get athlete historical GPS data
   */
  async getAthleteHistoricalData(athleteId, limit = 20) {
    return await this.makeRequest("getAthleteHistoricalData", {
      athleteId,
      limit,
    });
  }

  /**
   * Get athlete profile information
   */
  async getAthleteProfile(athleteId) {
    return await this.makeRequest("getAthleteProfile", { athleteId });
  }

  /**
   * Sync historical sessions for a team to populate athlete history
   */
  async syncHistoricalSessions(teamId) {
    return await this.makeRequest("syncHistoricalSessions", { teamId });
  }
}

// Export singleton instance for easy use
export const playerDataApi = new PlayerDataApiService();

// Export individual functions for backward compatibility
export async function getTeams() {
  return await playerDataApi.getTeams();
}

export async function getTeamSessions(teamId) {
  return await playerDataApi.getTeamSessions(teamId);
}

export async function getSessionGpsData(sessionId) {
  return await playerDataApi.getSessionGpsData(sessionId);
}

export async function getAthleteMetrics(athleteId, sessionId) {
  return await playerDataApi.getAthleteMetrics(athleteId, sessionId);
}

// Test function
export async function testPlayerDataConnection() {
  return await playerDataApi.testConnection();
}
